import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, AlertTriangle, User, Mail, ShieldAlert, 
  Calendar, Download, Edit2, Eye, Trash2, Send, Plus 
} from "lucide-react";

export default function WarningLetter() {
  const navigate = useNavigate();
  const emptyForm = {
    employeeId: "",
    employeeName: "",
    email: "",
    designation: "",
    department: "",
    warningType: "",
    incidentDate: "",
    warningLevel: "",
    description: "",
    correctiveAction: "",
    issuedBy: "",
    remarks: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [sentLetters, setSentLetters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sentWarningLetters") || "[]");
    } catch {
      return [];
    }
  });
  const [viewingLetter, setViewingLetter] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [view, setView] = useState("list"); // 'list' or 'form'

  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const handleChange = (key, value) => {
    setForm({ ...form, [key]: value });
  };

  const handleSend = () => {
    if (!form.employeeName || !form.warningType) {
      alert("Employee Name and Warning Type are required");
      return;
    }

    const payload = {
      id: editingId || Date.now(),
      ...form,
      sentOn: new Date().toLocaleDateString(),
    };

    let updated;
    if (editingId) {
      updated = sentLetters.map((l) => (l.id === editingId ? payload : l));
      setEditingId(null);
    } else {
      updated = [payload, ...sentLetters];
    }

    setSentLetters(updated);
    localStorage.setItem("sentWarningLetters", JSON.stringify(updated));
    setForm(emptyForm);
    setView("list");
  };

  const handleEdit = (letter) => {
    setForm(letter);
    setEditingId(letter.id);
    setView("form");
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this warning letter?")) {
      const updated = sentLetters.filter((l) => l.id !== id);
      setSentLetters(updated);
      localStorage.setItem("sentWarningLetters", JSON.stringify(updated));
    }
  };

  const downloadLetter = (l) => {
    const content = `OFFICIAL WARNING LETTER\n\nDate: ${l.sentOn}\nDear ${l.employeeName},\n\nThis letter serves as a ${l.warningLevel} due to ${l.warningType} on ${l.incidentDate}.\n\nIncident Description: ${l.description}\n\nCorrective Action Required: ${l.correctiveAction}\n\nRemarks: ${l.remarks}\n\nIssued By: ${l.issuedBy}`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Warning_${l.employeeName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <button 
              onClick={() => navigate("/admin/letter-template-mgmt")} 
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-2 transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} /> Back to Management
            </button>
            <h1 className="text-2xl font-semibold text-slate-900" style={poppinsStyle}>Warning Letter Management</h1>
            <p className="text-sm text-slate-500">Issue and track formal disciplinary warnings.</p>
          </div>
          
          {view === "list" && (
            <button 
              onClick={() => { setForm(emptyForm); setEditingId(null); setView("form"); }}
              className="flex items-center gap-2 bg-slate-900 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
            >
              <Plus size={16} /> Issue New Warning
            </button>
          )}
        </div>

        {view === "form" ? (
          <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            {/* Form Fields */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-lg font-semibold mb-6 text-slate-800 border-b pb-4" style={poppinsStyle}>
                {editingId ? "Edit Disciplinary Action" : "Incident Details"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Input label="Employee ID" value={form.employeeId} onChange={(v) => handleChange("employeeId", v)} />
                <Input label="Employee Name" icon={<User size={14}/>} value={form.employeeName} onChange={(v) => handleChange("employeeName", v)} />
                <Input label="Email" icon={<Mail size={14}/>} value={form.email} onChange={(v) => handleChange("email", v)} />
                <Input label="Designation" value={form.designation} onChange={(v) => handleChange("designation", v)} />
                
                <Select
                  label="Warning Type"
                  value={form.warningType}
                  onChange={(v) => handleChange("warningType", v)}
                  options={["Misconduct", "Performance", "Policy Violation", "Attendance", "Behavioral"]}
                />
                
                <Input type="date" label="Incident Date" icon={<Calendar size={14}/>} value={form.incidentDate} onChange={(v) => handleChange("incidentDate", v)} />
                
                <Select
                  label="Warning Level"
                  value={form.warningLevel}
                  onChange={(v) => handleChange("warningLevel", v)}
                  options={["First Warning", "Second Warning", "Final Warning"]}
                />

                <Input label="Issued By" value={form.issuedBy} onChange={(v) => handleChange("issuedBy", v)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <Textarea label="Incident Description" value={form.description} onChange={(v) => handleChange("description", v)} />
                <Textarea label="Corrective Action Required" value={form.correctiveAction} onChange={(v) => handleChange("correctiveAction", v)} />
              </div>
            </div>

            {/* Live Preview */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-8 relative overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-10"><ShieldAlert size={120} /></div>
               <h3 className="text-amber-800 font-bold uppercase tracking-widest text-xs mb-4">Live Document Preview</h3>
               <div className="space-y-4 text-slate-700 text-sm leading-relaxed">
                  <p>Dear <span className="font-bold">{form.employeeName || "[Employee Name]"}</span>,</p>
                  <p>This is a formal <span className="text-red-600 font-bold">{form.warningLevel || "[Level]"}</span> regarding <span className="font-semibold">{form.warningType || "[Reason]"}</span> occurred on <span className="font-semibold">{form.incidentDate || "[Date]"}</span>.</p>
                  <p><strong>Description:</strong> {form.description || "..."}</p>
                  <p><strong>Action Required:</strong> {form.correctiveAction || "..."}</p>
               </div>
               <div className="mt-8 flex gap-3">
                  <button onClick={handleSend} className="bg-slate-900 text-white px-8 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 flex items-center gap-2 transition-all">
                    <Send size={16} /> {editingId ? "Update Warning" : "Issue Warning"}
                  </button>
                  <button onClick={() => setView("list")} className="px-6 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Cancel</button>
               </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500">Employee</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500">Level/Type</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500">Date Issued</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sentLetters.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-20 text-center text-slate-400">
                      <AlertTriangle size={40} className="mx-auto mb-2 opacity-20" />
                      <p className="text-sm">No warning letters issued yet.</p>
                    </td>
                  </tr>
                ) : (
                  sentLetters.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{l.employeeName}</div>
                        <div className="text-xs text-slate-400">{l.designation}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 rounded text-[10px] font-bold uppercase ${
                          l.warningLevel.includes("Final") ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"
                        }`}>
                          {l.warningLevel}
                        </span>
                        <div className="text-xs text-slate-500 mt-1">{l.warningType}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{l.sentOn}</td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button title="View" onClick={() => setViewingLetter(l)} className="p-2 text-slate-400 hover:text-blue-600"><Eye size={16} /></button>
                          <button title="Edit" onClick={() => handleEdit(l)} className="p-2 text-slate-400 hover:text-indigo-600"><Edit2 size={16} /></button>
                          <button title="Download" onClick={() => downloadLetter(l)} className="p-2 text-slate-400 hover:text-green-600"><Download size={16} /></button>
                          <button title="Delete" onClick={() => handleDelete(l.id)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* View Modal Component */}
        {viewingLetter && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="bg-red-600 p-6 text-white flex justify-between items-center">
                <h2 className="font-bold text-lg" style={poppinsStyle}>Disciplinary Warning Record</h2>
                <button onClick={() => setViewingLetter(null)} className="hover:rotate-90 transition-transform"><AlertTriangle size={24} /></button>
              </div>
              <div className="p-8 space-y-4 text-slate-700">
                <div className="grid grid-cols-2 gap-4 text-xs font-bold uppercase text-slate-400">
                  <div>Employee: <span className="text-slate-900 block text-sm normal-case">{viewingLetter.employeeName}</span></div>
                  <div>Issued By: <span className="text-slate-900 block text-sm normal-case">{viewingLetter.issuedBy}</span></div>
                </div>
                <hr className="border-slate-100" />
                <p className="text-sm"><strong>Incident:</strong> {viewingLetter.description}</p>
                <p className="text-sm"><strong>Required Action:</strong> {viewingLetter.correctiveAction}</p>
                <div className="bg-slate-50 p-4 rounded-lg text-xs italic">
                  Note: This document is a part of the official employee record.
                </div>
              </div>
              <div className="p-6 border-t flex justify-end">
                <button onClick={() => setViewingLetter(null)} className="bg-slate-900 text-white px-6 py-2 rounded-lg text-sm font-medium">Close View</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable Components
function Input({ label, value, onChange, type = "text", icon }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <div className="relative flex items-center">
        {icon && <div className="absolute left-3 text-slate-400">{icon}</div>}
        <input 
          type={type} value={value} 
          onChange={(e) => onChange(e.target.value)} 
          className={`w-full border border-slate-200 rounded-lg ${icon ? 'pl-9' : 'px-4'} py-2 bg-slate-50/30 focus:ring-1 focus:ring-red-500 focus:bg-white outline-none transition-all text-sm`} 
        />
      </div>
    </div>
  );
}

function Textarea({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <textarea 
        className="w-full border border-slate-200 rounded-xl px-4 py-3 h-32 bg-slate-50/30 focus:ring-1 focus:ring-red-500 focus:bg-white outline-none transition-all text-sm" 
        value={value} 
        onChange={(e) => onChange(e.target.value)} 
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <select 
        value={value} 
        onChange={(e) => onChange(e.target.value)} 
        className="w-full border border-slate-200 rounded-lg px-4 py-2 bg-slate-50/30 focus:ring-1 focus:ring-red-500 focus:bg-white outline-none transition-all text-sm"
      >
        <option value="">Select Option</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}